import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { defaultShapeStyle, shapeStyles } from "../../lib/shape"
import { truncateLabel } from "./truncate-label"

const buttonVariantStyles = {
  primary:
    "border-0 bg-[var(--fl-color-background-primary-default)] text-[var(--fl-color-content-primary-default)] [&_svg]:text-[var(--fl-color-content-primary-default)] hover:bg-[var(--fl-color-background-primary-hover)] active:bg-[var(--fl-color-background-primary-pressed)] data-pressed:bg-[var(--fl-color-background-primary-pressed)] data-pressed:hover:bg-[var(--fl-color-background-primary-pressed)] focus-visible:bg-[var(--fl-color-background-primary-focus)] focus-visible:shadow-[0_0_0_var(--fl-focus-ring-width)_var(--fl-color-focus-ring-primary)] disabled:bg-[var(--fl-color-background-primary-disabled)] disabled:text-[var(--fl-color-content-filled-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-filled-disabled)]",
  secondary:
    "border-0 bg-[var(--fl-color-background-default-default)] text-[var(--fl-color-content-default-diap)] [&_svg]:text-[var(--fl-color-content-default-diap)] hover:bg-[var(--fl-color-background-default-hover)] active:bg-[var(--fl-color-background-default-pressed)] data-pressed:bg-[var(--fl-color-background-default-pressed)] data-pressed:hover:bg-[var(--fl-color-background-default-pressed)] focus-visible:bg-[var(--fl-color-background-default-focus)] focus-visible:shadow-[0_0_0_var(--fl-focus-ring-width)_var(--fl-color-focus-ring-secondary)] disabled:bg-[var(--fl-color-background-default-disabled)] disabled:text-[var(--fl-color-content-filled-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-filled-disabled)]",
  tertiary:
    "border-0 bg-[var(--fl-color-background-secondary-default)] text-[var(--fl-color-content-default-default)] [&_svg]:text-[var(--fl-color-content-default-default)] hover:bg-[var(--fl-color-background-secondary-hover)] active:bg-[var(--fl-color-background-secondary-pressed)] active:inset-shadow-minimal data-pressed:bg-[var(--fl-color-background-secondary-pressed)] data-pressed:hover:bg-[var(--fl-color-background-secondary-pressed)] data-pressed:inset-shadow-minimal focus-visible:bg-[var(--fl-color-background-secondary-focus)] focus-visible:shadow-[0_0_0_var(--fl-focus-ring-width)_var(--fl-color-focus-ring-tertiary)] disabled:bg-[var(--fl-color-background-secondary-disabled)] disabled:text-[var(--fl-color-content-default-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-default-disabled)]",
  outlined:
    "border-solid border-[length:var(--fl-layout-border-width-thin)] border-[var(--fl-color-border-outlined-default)] bg-transparent text-[var(--fl-color-content-default-default)] [&_svg]:text-[var(--fl-color-content-default-default)] hover:border-[var(--fl-color-border-outlined-hover)] active:border-[var(--fl-color-border-outlined-pressed)] data-pressed:border-[var(--fl-color-border-outlined-pressed)] data-pressed:hover:border-[var(--fl-color-border-outlined-pressed)] focus-visible:bg-[var(--fl-color-background-secondary-default)] focus-visible:shadow-[0_0_0_var(--fl-focus-ring-width)_var(--fl-color-focus-ring-outlined)] disabled:border-[var(--fl-color-border-outlined-disabled)] disabled:bg-[var(--fl-color-background-outlined-disabled)] disabled:text-[var(--fl-color-content-default-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-default-disabled)]",
  ghost:
    "border-0 bg-transparent text-[var(--fl-color-content-default-default)] [&_svg]:text-[var(--fl-color-content-default-default)] hover:bg-[var(--fl-color-background-ghost-hover)] active:bg-[var(--fl-color-background-ghost-pressed)] active:hover:bg-[var(--fl-color-background-ghost-pressed)] data-pressed:bg-[var(--fl-color-background-ghost-pressed)] data-pressed:hover:bg-[var(--fl-color-background-ghost-pressed)] focus-visible:shadow-[0_0_0_var(--fl-focus-ring-width)_var(--fl-color-focus-ring-ghost)] disabled:text-[var(--fl-color-content-default-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-default-disabled)]",
} as const

const buttonSizeStyles = {
  default:
    "h-[var(--fl-layout-buttons-height)] min-w-[90px] gap-[var(--fl-layout-buttons-spacing)] px-[var(--fl-layout-buttons-padding-horizontal)] py-[var(--fl-layout-buttons-padding-vertical)] text-ui-12",
  icon: "size-[var(--fl-layout-buttons-height)] gap-[var(--fl-layout-buttons-spacing)] px-[var(--fl-layout-buttons-padding-horizontal-altered)] py-[var(--fl-layout-buttons-padding-vertical)] text-ui-12",
} as const

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap outline-none select-none focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[var(--fl-layout-buttons-icon-size)]",
  {
    variants: {
      /**
       * Visual emphasis: `primary` for the main action, `secondary` and `tertiary` for quieter filled buttons,
       * `outlined` for a bordered button, `ghost` for a button without background (toolbars, icon buttons). Defaults to
       * `primary`.
       */
      variant: {
        ...buttonVariantStyles,
      },
      /**
       * `default` is a button with a label (minimum width 90px); `icon` is a square button that holds only an icon.
       * Defaults to `default`.
       */
      size: {
        ...buttonSizeStyles,
      },
      /**
       * Corner shape: `squared`, `rounded` or `circular`. Omitted, the button follows the default shape of its context.
       */
      shape: shapeStyles,
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      shape: null,
    },
    compoundVariants: [{ shape: null, class: defaultShapeStyle }],
  }
)

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    "data-part"?: string
    /** Cuts long text off with an ellipsis instead of widening the button; the button may then shrink below its minimum width. */
    truncate?: boolean
  }

/**
 * Clickable button for an action. Use `variant="primary"` for the main action of a view, `ghost` in toolbars, and
 * `size="icon"` for icon-only buttons (give them an `aria-label`).
 */
function Button({
  className,
  variant = "primary",
  size = "default",
  shape,
  "data-part": dataPart = "button",
  truncate = false,
  children,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-variant={variant}
      data-shape={shape ?? "default"}
      className={cn(buttonVariants({ variant, size, shape, className }), truncate && "min-w-0")}
      {...props}
      data-part={dataPart}
    >
      {truncate ? truncateLabel(children) : children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
